import { useState, useEffect, useRef } from "react"
import { Link, Plus, Edit, X, Users, BarChart3, ChevronLeft, ChevronRight, Loader2, ChevronDown, Check, AlertCircle } from "lucide-react"
import { FMOService } from "@/features/fmo"
import { CampaignService } from "@/features/campaigns"
import { VendorIntakeForm, Campaign } from "@/features/campaigns"
import { useCampaign } from "@/features/campaigns"

// Dropdown options
const COUNTRY_OPTIONS = [
  'United States',
  'Canada',
  'United Kingdom',
  'Australia',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Netherlands',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Belgium',
  'Switzerland',
  'Austria',
  'Ireland',
  'New Zealand',
  'Japan',
  'South Korea'
]

const STATE_OPTIONS = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia'
]

const GAME_SYSTEM_OPTIONS = [
  'Xbox',
  'PlayStation',
  'Steam'
]

interface EditIntakeLinksProps {
  campaignName?: string
}

// Local intake link interface for complex form data
interface LocalIntakeLink {
  id: string
  alias: string
  lawFirmName: string
  lawFirmLink: string
  intakeFormUrl: string
  isActive: boolean
  isHidden: boolean
  clientEligibility: {
    age: { min: number; max: number }
    gender: string[]
    country: string[]
    states: string[]
    gameSystem: string[]
    zipcodes: string[]
    allZipcodes: boolean
  }
    quotaMetrics: {
      totalSalesAllowed: number
      remainingSalesNeeded: number
      priority: string
      dueDate: string
      maxClientsPerIntake: number
      priorityOfFulfillment: number | string
      campaignAssignment: string
      bonusPerQualifiedSale: number
    }
  // Backend data
  backendData: VendorIntakeForm
}

// Form data interface for modal inputs (allows empty strings)
interface FormData {
  alias: string
  lawFirmName: string
  lawFirmLink: string
  intakeFormUrl: string
  clientEligibility: {
    age: { min: string; max: string }
    gender: string[]
    country: string[]
    states: string[]
    gameSystem: string[]
    zipcodes: string[]
    allZipcodes: boolean
  }
  quotaMetrics: {
    totalSalesAllowed: string
    remainingSalesNeeded: string
    priority: string
    dueDate: string
    maxClientsPerIntake: string
    priorityOfFulfillment: string
    campaignAssignment: string
    bonusPerQualifiedSale: string
  }
  isActive: boolean
  isHidden: boolean
}

// Multi-select dropdown component
interface MultiSelectDropdownProps {
  label: string
  options: string[]
  selectedValues: string[]
  onChange: (values: string[]) => void
  placeholder?: string
}

function MultiSelectDropdown({ label, options, selectedValues, onChange, placeholder }: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleToggleOption = (option: string) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter(val => val !== option))
    } else {
      onChange([...selectedValues, option])
    }
  }

  const handleSelectAll = () => {
    if (selectedValues.length === filteredOptions.length) {
      onChange([])
    } else {
      onChange(filteredOptions)
    }
  }

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder || 'Select options...'
    if (selectedValues.length === 1) return selectedValues[0]
    if (selectedValues.length <= 3) return selectedValues.join(', ')
    return `${selectedValues.length} selected`
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label.includes(' *') ? (
          <>
            {label.replace(' *', '')} <span className="text-red-500">*</span>
          </>
        ) : (
          label
        )}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-left flex items-center justify-between bg-white"
        >
          <span className={selectedValues.length === 0 ? 'text-gray-500' : 'text-gray-900'}>
            {getDisplayText()}
          </span>
          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
            {/* Search input */}
            <div className="p-3 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            
            {/* Select All option */}
            <div className="p-2 border-b border-gray-100">
              <button
                type="button"
                onClick={handleSelectAll}
                className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-md transition-colors"
              >
                {selectedValues.length === filteredOptions.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            
            {/* Options list */}
            <div className="max-h-40 overflow-y-auto">
              {filteredOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleToggleOption(option)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  <span>{option}</span>
                  {selectedValues.includes(option) && (
                    <Check className="w-4 h-4 text-green-600" />
                  )}
                </button>
              ))}
              {filteredOptions.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">No options found</div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Selected values display */}
      {selectedValues.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {selectedValues.map((value) => (
            <span
              key={value}
              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800"
            >
              {value}
              <button
                type="button"
                onClick={() => handleToggleOption(value)}
                className="ml-1 text-green-600 hover:text-green-800"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default function EditIntakeLinks({ campaignName }: EditIntakeLinksProps) {
  const { selectedCampaign } = useCampaign()
  
  // Intake Links state - using local complex structure
  const [intakeLinks, setIntakeLinks] = useState<LocalIntakeLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  
  // Campaigns state for dropdown
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [campaignsLoading, setCampaignsLoading] = useState(false)
  
  const [showAddLinkModal, setShowAddLinkModal] = useState(false)
  const [editingLink, setEditingLink] = useState<LocalIntakeLink | null>(null)
  const [modalError, setModalError] = useState<string | null>(null)
  
  // Field-specific errors
  const [fieldErrors, setFieldErrors] = useState({
    alias: '',
    lawFirmName: '',
    lawFirmLink: '',
    intakeFormUrl: '',
    // Client Eligibility errors
    minAge: '',
    maxAge: '',
    gender: '',
    countries: '',
    states: '',
    gameSystems: '',
    zipcodes: '',
    // Quota Metrics errors
    totalSalesAllowed: '',
    remainingSalesNeeded: '',
    maxClientsPerIntake: '',
    priorityOfFulfillment: '',
    bonusPerQualifiedSale: '',
    dueDate: '',
    campaignAssignment: ''
  })
  
  // Form state for modal
  const [formData, setFormData] = useState<FormData>({
    alias: '',
    lawFirmName: '',
    lawFirmLink: '',
    intakeFormUrl: '',
    clientEligibility: {
      age: { min: '', max: '' },
      gender: ['All'],
      country: [''],
      states: [''],
      gameSystem: [''],
      zipcodes: [''],
      allZipcodes: false
    },
    quotaMetrics: {
      totalSalesAllowed: '',
      remainingSalesNeeded: '',
      priority: 'High',
      dueDate: '',
      maxClientsPerIntake: '',
      priorityOfFulfillment: '',
      campaignAssignment: '',
      bonusPerQualifiedSale: ''
    },
    isActive: true,
    isHidden: false
  })
  
  // Local state for zipcode input (to preserve commas while typing)
  const [zipcodeInput, setZipcodeInput] = useState('')
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 5
  
  // Assign Intake Links state
  const [assignLinksModal, setAssignLinksModal] = useState(false)
  const [draggedLink, setDraggedLink] = useState<VendorIntakeForm | null>(null)
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null)

  // Fetch intake links and campaigns on component mount and when selectedCampaign changes
  useEffect(() => {
    fetchIntakeLinks()
    fetchCampaigns()
  }, [selectedCampaign])

  // Reset to page 1 when campaign changes
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedCampaign])

  // Auto-fill campaign when opening add modal
  useEffect(() => {
    if (showAddLinkModal && selectedCampaign && !editingLink) {
      setFormData(prev => ({
        ...prev,
        quotaMetrics: {
          ...prev.quotaMetrics,
          campaignAssignment: selectedCampaign.name
        }
      }))
    }
  }, [showAddLinkModal, selectedCampaign, editingLink])

  // Fetch campaigns for dropdown
  const fetchCampaigns = async () => {
    try {
      setCampaignsLoading(true)
      const response = await CampaignService.getCampaigns()
      if (response.data) {
        setCampaigns(response.data)
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err)
      // Don't show error to user for campaigns, just log it
    } finally {
      setCampaignsLoading(false)
    }
  }

  // Reset form data when modal opens/closes
  const resetFormData = () => {
    setFormData({
      alias: '',
      lawFirmName: '',
      lawFirmLink: '',
      intakeFormUrl: '',
      clientEligibility: {
        age: { min: '', max: '' },
        gender: ['All'],
        country: [],
        states: [],
        gameSystem: [],
        zipcodes: [],
        allZipcodes: false
      },
      quotaMetrics: {
        totalSalesAllowed: '',
        remainingSalesNeeded: '',
        priority: 'High',
        dueDate: '',
        maxClientsPerIntake: '',
        priorityOfFulfillment: '',
        campaignAssignment: '',
        bonusPerQualifiedSale: ''
      },
      isActive: true,
      isHidden: false
    })
    setZipcodeInput('')
    setFieldErrors({
      alias: '',
      lawFirmName: '',
      lawFirmLink: '',
      intakeFormUrl: '',
      minAge: '',
      maxAge: '',
      gender: '',
      countries: '',
      states: '',
      gameSystems: '',
      zipcodes: '',
      totalSalesAllowed: '',
      remainingSalesNeeded: '',
      maxClientsPerIntake: '',
      priorityOfFulfillment: '',
      bonusPerQualifiedSale: '',
      dueDate: '',
      campaignAssignment: ''
    })
  }

  // Helper function to generate zipcodes based on selected countries and states
  const generateZipcodesForSelection = () => {
    const zipcodes: string[] = []
    
    // US State zipcodes mapping (representative zipcodes for major cities in each state)
    const stateZipcodes: { [key: string]: string[] } = {
      'Alabama': ['35203', '36104', '35801'],  // Birmingham, Montgomery, Huntsville
      'Alaska': ['99501', '99701', '99801'],  // Anchorage, Fairbanks, Juneau
      'Arizona': ['85001', '85701', '86001'],  // Phoenix, Tucson, Flagstaff
      'Arkansas': ['72201', '72701', '71601'],  // Little Rock, Fayetteville, Pine Bluff
      'California': ['90012', '94102', '92101', '95814'],  // Los Angeles, San Francisco, San Diego, Sacramento
      'Colorado': ['80202', '80903', '80301'],  // Denver, Colorado Springs, Boulder
      'Connecticut': ['06103', '06510', '06901'],  // Hartford, New Haven, Stamford
      'Delaware': ['19901', '19801'],  // Dover, Wilmington
      'Florida': ['32301', '33101', '32801', '33602'],  // Tallahassee, Miami, Orlando, Tampa
      'Georgia': ['30303', '31401', '30901'],  // Atlanta, Savannah, Augusta
      'Hawaii': ['96801', '96720'],  // Honolulu, Hilo
      'Idaho': ['83702', '83201'],  // Boise, Pocatello
      'Illinois': ['60601', '62701', '61101'],  // Chicago, Springfield, Rockford
      'Indiana': ['46204', '46802', '47401'],  // Indianapolis, Fort Wayne, Bloomington
      'Iowa': ['50309', '52240', '51501'],  // Des Moines, Iowa City, Council Bluffs
      'Kansas': ['66102', '67202', '66603'],  // Kansas City, Wichita, Topeka
      'Kentucky': ['40202', '40507', '42101'],  // Louisville, Lexington, Bowling Green
      'Louisiana': ['70112', '70802', '71101'],  // New Orleans, Baton Rouge, Shreveport
      'Maine': ['04101', '04401'],  // Portland, Bangor
      'Maryland': ['21201', '20850', '21401'],  // Baltimore, Rockville, Annapolis
      'Massachusetts': ['02101', '01103', '02139'],  // Boston, Springfield, Cambridge
      'Michigan': ['48201', '49503', '48823'],  // Detroit, Grand Rapids, East Lansing
      'Minnesota': ['55401', '55102', '55802'],  // Minneapolis, St Paul, Duluth
      'Mississippi': ['39201', '39501', '38801'],  // Jackson, Gulfport, Tupelo
      'Missouri': ['63101', '64101', '65801'],  // St Louis, Kansas City, Springfield
      'Montana': ['59101', '59601'],  // Billings, Helena
      'Nebraska': ['68102', '68501', '69101'],  // Omaha, Lincoln, North Platte
      'Nevada': ['89101', '89501'],  // Las Vegas, Reno
      'New Hampshire': ['03101', '03301'],  // Manchester, Concord
      'New Jersey': ['07102', '08608', '07302'],  // Newark, Trenton, Jersey City
      'New Mexico': ['87101', '88001'],  // Albuquerque, Las Cruces
      'New York': ['10001', '14202', '12207', '13202'],  // New York City, Buffalo, Albany, Syracuse
      'North Carolina': ['27601', '28202', '27401'],  // Raleigh, Charlotte, Greensboro
      'North Dakota': ['58501', '58102'],  // Bismarck, Fargo
      'Ohio': ['43201', '44101', '45202'],  // Columbus, Cleveland, Cincinnati
      'Oklahoma': ['73102', '74103'],  // Oklahoma City, Tulsa
      'Oregon': ['97201', '97301'],  // Portland, Salem
      'Pennsylvania': ['19102', '15201', '17101'],  // Philadelphia, Pittsburgh, Harrisburg
      'Rhode Island': ['02903', '02840'],  // Providence, Newport
      'South Carolina': ['29201', '29401'],  // Columbia, Charleston
      'South Dakota': ['57101', '57501'],  // Sioux Falls, Pierre
      'Tennessee': ['37201', '38103', '37901'],  // Nashville, Memphis, Knoxville
      'Texas': ['75201', '77002', '78701', '79901'],  // Dallas, Houston, Austin, El Paso
      'Utah': ['84101', '84601'],  // Salt Lake City, Provo
      'Vermont': ['05601', '05401'],  // Montpelier, Burlington
      'Virginia': ['23219', '22201', '23510'],  // Richmond, Arlington, Norfolk
      'Washington': ['98101', '99201', '98501'],  // Seattle, Spokane, Olympia
      'West Virginia': ['25301', '26501'],  // Charleston, Morgantown
      'Wisconsin': ['53202', '53703', '54901'],  // Milwaukee, Madison, Oshkosh
      'Wyoming': ['82001', '82601'],  // Cheyenne, Casper
      'District of Columbia': ['20001', '20500']  // Washington DC
    }
    
    // Add zipcodes for selected states
    formData.clientEligibility.states.forEach(state => {
      if (stateZipcodes[state]) {
        zipcodes.push(...stateZipcodes[state])
      }
    })
    
    // If United States is selected in countries but no specific states, add major US cities
    if (formData.clientEligibility.country.includes('United States') && formData.clientEligibility.states.length === 0) {
      zipcodes.push('10001', '90012', '60601', '77002', '19102', '85001', '33101', '98101', '02101', '30303')
      // NYC, LA, Chicago, Houston, Philadelphia, Phoenix, Miami, Seattle, Boston, Atlanta
    }
    
    return zipcodes
  }

  // Initialize form data when editing
  const initializeFormData = (link: LocalIntakeLink) => {
    setFormData({
      alias: link.alias,
      lawFirmName: link.lawFirmName,
      lawFirmLink: link.lawFirmLink,
      intakeFormUrl: link.intakeFormUrl,
      clientEligibility: {
        ...link.clientEligibility,
        age: {
          min: String(link.clientEligibility.age.min),
          max: String(link.clientEligibility.age.max)
        },
        allZipcodes: link.clientEligibility.allZipcodes || false
      },
      quotaMetrics: {
        ...link.quotaMetrics,
        totalSalesAllowed: String(link.quotaMetrics.totalSalesAllowed),
        remainingSalesNeeded: String(link.quotaMetrics.remainingSalesNeeded),
        maxClientsPerIntake: String(link.quotaMetrics.maxClientsPerIntake),
        priorityOfFulfillment: String(link.quotaMetrics.priorityOfFulfillment),
        bonusPerQualifiedSale: String(link.quotaMetrics.bonusPerQualifiedSale)
      },
      isActive: link.isActive,
      isHidden: link.isHidden
    })
    setZipcodeInput(link.clientEligibility.zipcodes.join(', '))
  }

  const fetchIntakeLinks = async () => {
    // Don't fetch if no campaign is selected
    if (!selectedCampaign) {
      setIntakeLinks([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const data = await FMOService.getVendorIntakeLinks()
      
      // CRITICAL: Filter by selectedCampaign.id
      const campaignFilteredData = data.filter(
        (item: VendorIntakeForm) => item.campaignId === selectedCampaign.id
      )
      
      // Transform backend data to local complex structure
      const transformedData = campaignFilteredData.map((item: VendorIntakeForm) => ({
        id: item.id,
        alias: item.alias || item.vendorName, // Link Alias (Public View) - fallback to vendorName if not set
        lawFirmName: item.vendorName, // Law Firm Name (Internal)
        lawFirmLink: item.lawFirmLink || "", // Law Firm Link (Internal)
        intakeFormUrl: item.intakeUrl || "", // Intake Form URL
        isActive: item.status === 'active',
        isHidden: item.status === 'inactive',
        clientEligibility: item.clientEligibility ? {
          ...item.clientEligibility,
          allZipcodes: (item.clientEligibility as any).allZipcodes ?? false
        } : {
          age: { min: 18, max: 65 }, // Default values
          gender: ["All"],
          country: ["United States"],
          states: ["All States"],
          gameSystem: ["All Systems"],
          zipcodes: ["All Zipcodes"],
          allZipcodes: false
        },
        quotaMetrics: item.quotaMetrics || {
          totalSalesAllowed: 100, // Default values
          remainingSalesNeeded: 25,
          priority: "High",
          dueDate: "2024-12-31",
          maxClientsPerIntake: 50, // Default values
          priorityOfFulfillment: 1,
          campaignAssignment: item.businessType || "Default Campaign",
          bonusPerQualifiedSale: 150
        },
        backendData: item
      }))
      
      setIntakeLinks(transformedData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch intake links')
    } finally {
      setLoading(false)
    }
  }

  // Intake Links management functions
  const addIntakeLink = async (formData: any) => {
    if (!selectedCampaign) {
      setModalError('No campaign selected. Please select a campaign first.')
      return
    }

    try {
      setActionLoading('create')
      setModalError(null)
      
      const vendorIntakeData = {
        campaignId: selectedCampaign.id, // MUST include selected campaignId
        vendorName: formData.lawFirmName, // Law Firm Name (Internal)
        alias: formData.alias, // Link Alias (Public View)
        contactPerson: formData.alias, // Also store in contactPerson for backward compatibility
        email: `contact@${formData.lawFirmName.toLowerCase().replace(/\s+/g, '')}.com` || "contact@lawfirm.com", // Generate email from law firm name
        phone: "+1-555-012-3456", // Default phone - could be made configurable
        lawFirmLink: formData.lawFirmLink, // Law Firm Link (Internal)
        intakeUrl: formData.intakeFormUrl, // Intake Form URL
        address: {
          street: "123 Main St",
          city: "City", 
          state: "State",
          zipCode: "12345",
          country: "USA"
        },
        businessType: formData.quotaMetrics.campaignAssignment || "Law Firm",
        clientEligibility: {
          ...formData.clientEligibility,
          age: {
            min: formData.clientEligibility.age.min === '' ? 18 : Number(formData.clientEligibility.age.min) || 18,
            max: formData.clientEligibility.age.max === '' ? 65 : Number(formData.clientEligibility.age.max) || 65
          },
          allZipcodes: formData.clientEligibility.allZipcodes,
          zipcodes: formData.clientEligibility.allZipcodes ? [] : formData.clientEligibility.zipcodes
        },
        quotaMetrics: {
          ...formData.quotaMetrics,
          totalSalesAllowed: formData.quotaMetrics.totalSalesAllowed === '' ? 100 : Number(formData.quotaMetrics.totalSalesAllowed) || 100,
          remainingSalesNeeded: formData.quotaMetrics.remainingSalesNeeded === '' ? 25 : Number(formData.quotaMetrics.remainingSalesNeeded) || 25,
          maxClientsPerIntake: formData.quotaMetrics.maxClientsPerIntake === '' ? 50 : Number(formData.quotaMetrics.maxClientsPerIntake) || 50,
          priorityOfFulfillment: formData.quotaMetrics.priorityOfFulfillment === '' 
            ? 1 
            : Number(formData.quotaMetrics.priorityOfFulfillment) || 1,
          bonusPerQualifiedSale: formData.quotaMetrics.bonusPerQualifiedSale === '' ? 150 : Number(formData.quotaMetrics.bonusPerQualifiedSale) || 150
        },
        status: formData.isActive ? "active" : "inactive"
      }
      
      const newBackendLink = await FMOService.createVendorIntakeLink(vendorIntakeData)
      
      // Create local complex structure
      const newLocalLink: LocalIntakeLink = {
        id: newBackendLink.id,
        alias: formData.alias,
        lawFirmName: formData.lawFirmName,
        lawFirmLink: formData.lawFirmLink,
        intakeFormUrl: formData.intakeFormUrl,
        isActive: formData.isActive,
        isHidden: formData.isHidden,
        clientEligibility: {
          ...formData.clientEligibility,
          age: {
            min: formData.clientEligibility.age.min === '' ? 18 : Number(formData.clientEligibility.age.min) || 18,
            max: formData.clientEligibility.age.max === '' ? 65 : Number(formData.clientEligibility.age.max) || 65
          },
          zipcodes: formData.clientEligibility.allZipcodes ? [] : formData.clientEligibility.zipcodes
        },
        quotaMetrics: {
          ...formData.quotaMetrics,
          totalSalesAllowed: formData.quotaMetrics.totalSalesAllowed === '' ? 100 : Number(formData.quotaMetrics.totalSalesAllowed) || 100,
          remainingSalesNeeded: formData.quotaMetrics.remainingSalesNeeded === '' ? 25 : Number(formData.quotaMetrics.remainingSalesNeeded) || 25,
          maxClientsPerIntake: formData.quotaMetrics.maxClientsPerIntake === '' ? 50 : Number(formData.quotaMetrics.maxClientsPerIntake) || 50,
          priorityOfFulfillment: typeof formData.quotaMetrics.priorityOfFulfillment === 'string' 
            ? (formData.quotaMetrics.priorityOfFulfillment === '' ? 1 : Number(formData.quotaMetrics.priorityOfFulfillment) || 1)
            : formData.quotaMetrics.priorityOfFulfillment,
          bonusPerQualifiedSale: formData.quotaMetrics.bonusPerQualifiedSale === '' ? 150 : Number(formData.quotaMetrics.bonusPerQualifiedSale) || 150
        },
        backendData: newBackendLink
      }
      
      setIntakeLinks(prev => [...prev, newLocalLink])
      setShowAddLinkModal(false)
      setModalError(null)
    } catch (err) {
      console.error('Add intake link error:', err)
      let errorMessage = 'Failed to create intake link'
      
      if (err instanceof Error) {
        errorMessage = err.message
      } else if (typeof err === 'object' && err !== null) {
        // Handle API error responses
        const apiError = err as any
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        } else if (apiError.response?.data?.error) {
          errorMessage = apiError.response.data.error
        } else if (apiError.message) {
          errorMessage = apiError.message
        }
      }
      
      setModalError(errorMessage)
    } finally {
      setActionLoading(null)
    }
  }
  
  const updateIntakeLink = async () => {
    if (!editingLink) return
    
    try {
      setActionLoading(editingLink.id)
      setModalError(null)
      
      // Transform form data to backend format
      const updateData = {
        vendorName: formData.lawFirmName, // Law Firm Name (Internal)
        alias: formData.alias, // Link Alias (Public View)
        contactPerson: formData.alias, // Also store in contactPerson for backward compatibility
        email: `contact@${formData.lawFirmName.toLowerCase().replace(/\s+/g, '')}.com` || editingLink.backendData.email, // Generate email from law firm name or keep existing
        phone: editingLink.backendData.phone, // Keep existing phone
        lawFirmLink: formData.lawFirmLink, // Law Firm Link (Internal)
        intakeUrl: formData.intakeFormUrl, // Intake Form URL
        address: editingLink.backendData.address, // Keep existing address
        businessType: formData.quotaMetrics.campaignAssignment || editingLink.backendData.businessType,
        clientEligibility: {
          ...formData.clientEligibility,
          age: {
            min: formData.clientEligibility.age.min === '' ? 18 : Number(formData.clientEligibility.age.min) || 18,
            max: formData.clientEligibility.age.max === '' ? 65 : Number(formData.clientEligibility.age.max) || 65
          },
          allZipcodes: formData.clientEligibility.allZipcodes,
          zipcodes: formData.clientEligibility.allZipcodes ? [] : formData.clientEligibility.zipcodes
        },
        quotaMetrics: {
          ...formData.quotaMetrics,
          totalSalesAllowed: formData.quotaMetrics.totalSalesAllowed === '' ? 100 : Number(formData.quotaMetrics.totalSalesAllowed) || 100,
          remainingSalesNeeded: formData.quotaMetrics.remainingSalesNeeded === '' ? 25 : Number(formData.quotaMetrics.remainingSalesNeeded) || 25,
          maxClientsPerIntake: formData.quotaMetrics.maxClientsPerIntake === '' ? 50 : Number(formData.quotaMetrics.maxClientsPerIntake) || 50,
          priorityOfFulfillment: formData.quotaMetrics.priorityOfFulfillment === '' 
            ? 1 
            : Number(formData.quotaMetrics.priorityOfFulfillment) || 1,
          bonusPerQualifiedSale: formData.quotaMetrics.bonusPerQualifiedSale === '' ? 150 : Number(formData.quotaMetrics.bonusPerQualifiedSale) || 150
        },
        status: formData.isActive ? 'active' : 'inactive'
      }
      
      const result = await FMOService.updateVendorIntakeLink(editingLink.id, updateData)
      
      // Update local state with new data
      setIntakeLinks(prev => prev.map(link => 
        link.id === editingLink.id ? {
          ...link,
          alias: formData.alias,
          lawFirmName: formData.lawFirmName,
          lawFirmLink: formData.lawFirmLink,
          intakeFormUrl: formData.intakeFormUrl,
          clientEligibility: {
            ...formData.clientEligibility,
            age: {
              min: formData.clientEligibility.age.min === '' ? 18 : Number(formData.clientEligibility.age.min) || 18,
              max: formData.clientEligibility.age.max === '' ? 65 : Number(formData.clientEligibility.age.max) || 65
            },
            allZipcodes: formData.clientEligibility.allZipcodes,
            zipcodes: formData.clientEligibility.allZipcodes ? [] : formData.clientEligibility.zipcodes
          },
          quotaMetrics: {
            ...formData.quotaMetrics,
            totalSalesAllowed: formData.quotaMetrics.totalSalesAllowed === '' ? 100 : Number(formData.quotaMetrics.totalSalesAllowed) || 100,
            remainingSalesNeeded: formData.quotaMetrics.remainingSalesNeeded === '' ? 25 : Number(formData.quotaMetrics.remainingSalesNeeded) || 25,
            maxClientsPerIntake: formData.quotaMetrics.maxClientsPerIntake === '' ? 50 : Number(formData.quotaMetrics.maxClientsPerIntake) || 50,
            priorityOfFulfillment: formData.quotaMetrics.priorityOfFulfillment === '' 
              ? 1 
              : Number(formData.quotaMetrics.priorityOfFulfillment) || 1,
            bonusPerQualifiedSale: formData.quotaMetrics.bonusPerQualifiedSale === '' ? 150 : Number(formData.quotaMetrics.bonusPerQualifiedSale) || 150
          },
          isActive: formData.isActive,
          isHidden: formData.isHidden,
          backendData: result
        } : link
      ))
      
      setEditingLink(null)
      resetFormData()
    } catch (err) {
      console.error('Update intake link error:', err)
      let errorMessage = 'Failed to update intake link'
      
      if (err instanceof Error) {
        errorMessage = err.message
      } else if (typeof err === 'object' && err !== null) {
        const apiError = err as any
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        } else if (apiError.response?.data?.error) {
          errorMessage = apiError.response.data.error
        } else if (apiError.message) {
          errorMessage = apiError.message
        }
      }
      
      setModalError(errorMessage)
    } finally {
      setActionLoading(null)
    }
  }
  
  const deleteIntakeLink = async (linkId: string) => {
    if (!window.confirm("Are you sure you want to delete this intake link?")) return
    
    try {
      setActionLoading(linkId)
      setError(null)
      
      await FMOService.deleteVendorIntakeLink(linkId)
      setIntakeLinks(prev => prev.filter(link => link.id !== linkId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete intake link')
    } finally {
      setActionLoading(null)
    }
  }
  
  const toggleLinkStatus = async (linkId: string, newStatus: string) => {
    try {
      setActionLoading(linkId)
      setError(null)
      
      const currentLink = intakeLinks.find(link => link.id === linkId)
      if (!currentLink) return
      
      // Update local state first for immediate feedback
      setIntakeLinks(prev => prev.map(link => 
        link.id === linkId ? { 
          ...link, 
          isActive: newStatus === 'active',
          isHidden: newStatus === 'hidden',
          backendData: { ...link.backendData, status: newStatus }
        } : link
      ))
      
      // Make API call
      await FMOService.updateVendorIntakeLink(linkId, {
        status: newStatus
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update intake link status')
      // Revert local state on error - fetch fresh data
      fetchIntakeLinks()
    } finally {
      setActionLoading(null)
    }
  }

  // Filter intake links by selected campaign from context
  const filteredIntakeLinks = selectedCampaign 
    ? intakeLinks.filter(link => link.backendData.campaignId === selectedCampaign.id)
    : []

  // Pagination calculations
  const totalPages = Math.ceil(filteredIntakeLinks.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentLinks = filteredIntakeLinks.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }


  // Shimmer skeleton component
  const ShimmerRow = () => (
    <tr className="animate-pulse">
      <td className="px-6 py-4">
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-24"></div>
          <div className="h-3 bg-gray-100 rounded w-16"></div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-32"></div>
          <div className="h-3 bg-gray-100 rounded w-20"></div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-200 rounded w-48"></div>
      </td>
      <td className="px-6 py-4">
        <div className="space-y-1">
          <div className="h-3 bg-gray-200 rounded w-16"></div>
          <div className="h-3 bg-gray-200 rounded w-20"></div>
          <div className="h-3 bg-gray-200 rounded w-14"></div>
          <div className="h-3 bg-gray-200 rounded w-18"></div>
          <div className="h-3 bg-gray-200 rounded w-22"></div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="space-y-1">
          <div className="h-3 bg-gray-200 rounded w-12"></div>
          <div className="h-3 bg-gray-200 rounded w-16"></div>
          <div className="h-3 bg-gray-200 rounded w-14"></div>
          <div className="h-3 bg-gray-200 rounded w-20"></div>
          <div className="h-3 bg-gray-200 rounded w-18"></div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="space-y-2">
          <div className="h-6 bg-gray-200 rounded-full w-16"></div>
          <div className="h-6 bg-gray-200 rounded-full w-20"></div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <div className="h-8 bg-gray-200 rounded-lg w-16"></div>
          <div className="h-8 bg-gray-200 rounded-lg w-20"></div>
        </div>
      </td>
    </tr>
  )

  // Delete shimmer effect for individual cells
  const DeleteShimmer = ({ children }: { children: React.ReactNode }) => (
    <div className="relative">
      <div className="opacity-50">{children}</div>
      <div className="absolute inset-0 bg-gradient-to-r from-red-50 via-red-100 to-red-50 animate-pulse rounded opacity-30"></div>
    </div>
  )

  // Loading state with shimmer
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        {/* Header with shimmer */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden mb-4">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 border-b border-gray-200">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Link className="w-6 h-6 text-white" />
                </div>
                <div className="space-y-2">
                  <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
                  <div className="h-4 bg-gray-100 rounded w-64 animate-pulse"></div>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl">
                <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-orange-800">Link Management</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-12 bg-gray-200 rounded-xl w-32 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Table with shimmer rows */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800">
                <tr>
                  <th className="px-6 py-4 text-left text-white font-semibold">Link Alias</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Law Firm</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Intake URL</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Eligibility</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Quota Metrics</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Status</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {Array.from({ length: 5 }).map((_, index) => (
                  <ShimmerRow key={index} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="mt-2 text-red-600 hover:text-red-800 text-sm"
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Main Settings Container */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
        
        {/* Top Row - Edit Intake Links */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 border-b border-gray-200">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <Link className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Edit Intake Links</h2>
                <p className="text-gray-600">Manage Formstack URLs and intake form configurations for {campaignName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl">
                <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-orange-800">Link Management</span>
              </div>
              
              {/* Selected Campaign Display */}
              {selectedCampaign && (
                <div className="flex items-center gap-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-xl">
                  <span className="text-sm font-semibold text-primary-900">{selectedCampaign.name}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => {
                  setShowAddLinkModal(true)
                  setModalError(null)
                  setEditingLink(null)
                  resetFormData()
                }}
                disabled={actionLoading === 'create'}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium flex items-center gap-2 shadow-lg hover:shadow-xl whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading === 'create' ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="animate-pulse">Creating...</span>
                  </div>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Add Link
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* No Campaign Selected Warning */}
        {!selectedCampaign && (
          <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-yellow-800">No Campaign Selected</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Please select a campaign from the dashboard to view and manage intake links.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Intake Links Table - Compact Design */}
        <div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-white font-semibold text-sm w-32">Link Alias</th>
                  <th className="px-4 py-3 text-left text-white font-semibold text-sm w-40">Law Firm</th>
                  <th className="px-4 py-3 text-left text-white font-semibold text-sm w-48">Intake URL</th>
                  <th className="px-4 py-3 text-left text-white font-semibold text-sm w-64">Eligibility</th>
                  <th className="px-4 py-3 text-left text-white font-semibold text-sm w-56">Quota</th>
                  <th className="px-4 py-3 text-left text-white font-semibold text-sm w-24">Status</th>
                  <th className="px-4 py-3 text-left text-white font-semibold text-sm w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentLinks.map((link, index) => (
                <tr key={link.id} className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} ${actionLoading === link.id ? 'opacity-60 animate-pulse' : ''}`}>
                  {/* Link Alias - Compact */}
                  <td className="px-4 py-3">
                    {actionLoading === link.id ? (
                      <DeleteShimmer>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm truncate">{link.alias}</div>
                          <div className="text-xs text-gray-500">Public</div>
                        </div>
                      </DeleteShimmer>
                    ) : (
                      <div>
                        <div className="font-semibold text-gray-900 text-sm truncate">{link.alias}</div>
                        <div className="text-xs text-gray-500">Public</div>
                      </div>
                    )}
                  </td>
                  
                  {/* Law Firm - Compact */}
                  <td className="px-4 py-3">
                    {actionLoading === link.id ? (
                      <DeleteShimmer>
                        <div>
                          <div className="font-medium text-gray-900 text-sm truncate" title={link.lawFirmName}>
                            {link.lawFirmName}
                          </div>
                          <a href={link.lawFirmLink} target="_blank" rel="noopener noreferrer" 
                             className="text-xs text-blue-600 hover:underline">
                            View →
                          </a>
                        </div>
                      </DeleteShimmer>
                    ) : (
                      <div>
                        <div className="font-medium text-gray-900 text-sm truncate" title={link.lawFirmName}>
                          {link.lawFirmName}
                        </div>
                        <a href={link.lawFirmLink} target="_blank" rel="noopener noreferrer" 
                           className="text-xs text-blue-600 hover:underline">
                          View →
                        </a>
                      </div>
                    )}
                  </td>
                  
                  {/* Intake URL - Truncated with copy */}
                  <td className="px-4 py-3">
                    {actionLoading === link.id ? (
                      <DeleteShimmer>
                        <div className="flex items-center gap-2">
                          <a href={link.intakeFormUrl} target="_blank" rel="noopener noreferrer"
                             className="text-xs text-blue-600 hover:underline truncate max-w-[180px]" 
                             title={link.intakeFormUrl}>
                            {link.intakeFormUrl}
                          </a>
                          <button 
                            onClick={() => navigator.clipboard.writeText(link.intakeFormUrl)}
                            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                            title="Copy URL">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </DeleteShimmer>
                    ) : (
                      <div className="flex items-center gap-2">
                        <a href={link.intakeFormUrl} target="_blank" rel="noopener noreferrer"
                           className="text-xs text-blue-600 hover:underline truncate max-w-[180px]" 
                           title={link.intakeFormUrl}>
                          {link.intakeFormUrl}
                        </a>
                        <button 
                          onClick={() => navigator.clipboard.writeText(link.intakeFormUrl)}
                          className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                          title="Copy URL">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </td>
                  
                  {/* Eligibility - Horizontal Chips */}
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 items-center text-xs">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                        {link.clientEligibility.age.min}-{link.clientEligibility.age.max}y
                      </span>
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">
                        {link.clientEligibility.gender[0]}
                      </span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                        {link.clientEligibility.country.length} {link.clientEligibility.country.length === 1 ? 'country' : 'countries'}
                      </span>
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-medium">
                        {link.clientEligibility.states.length} {link.clientEligibility.states.length === 1 ? 'state' : 'states'}
                      </span>
                      <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">
                        {link.clientEligibility.gameSystem.join(', ')}
                      </span>
                      {link.clientEligibility.allZipcodes ? (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                          ✅ All Zipcodes
                        </span>
                      ) : (
                        link.clientEligibility.zipcodes.length > 0 && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium cursor-help"
                                title={link.clientEligibility.zipcodes.join(', ')}>
                            {link.clientEligibility.zipcodes.length} zips
                          </span>
                        )
                      )}
                    </div>
                  </td>
                  
                  {/* Quota Metrics - Compact Card */}
                  <td className="px-4 py-3">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Progress:</span>
                        <span className="font-semibold">
                          {link.quotaMetrics.totalSalesAllowed - link.quotaMetrics.remainingSalesNeeded}/{link.quotaMetrics.totalSalesAllowed}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                          className="bg-green-600 h-1.5 rounded-full transition-all"
                          style={{ 
                            width: `${((link.quotaMetrics.totalSalesAllowed - link.quotaMetrics.remainingSalesNeeded) / link.quotaMetrics.totalSalesAllowed) * 100}%` 
                          }}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          link.quotaMetrics.priority === 'High' ? 'bg-red-100 text-red-700' :
                          link.quotaMetrics.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {link.quotaMetrics.priority}
                        </span>
                        <span className="text-xs text-gray-600">
                          Due: {new Date(link.quotaMetrics.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <div className="text-xs text-green-600 font-semibold">
                        💰 ${link.quotaMetrics.bonusPerQualifiedSale} bonus
                      </div>
                    </div>
                  </td>
                  
                  {/* Status - Single Badge */}
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`px-2 py-1 rounded-md text-xs font-medium text-center ${
                        link.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {link.isActive ? '✓ Active' : '✗ Inactive'}
                      </span>
                      <span className={`px-2 py-1 rounded-md text-xs font-medium text-center ${
                        !link.isHidden ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {link.isHidden ? '👁️‍🗨️ Hidden' : '👁️ Visible'}
                      </span>
                    </div>
                  </td>
                  
                  {/* Actions - Icon buttons */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditingLink(link)
                          initializeFormData(link)
                        }}
                        disabled={actionLoading === link.id}
                        className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all"
                        title="Edit">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteIntakeLink(link.id)}
                        disabled={actionLoading === link.id}
                        className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all"
                        title="Delete">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 px-4">
              <div className="text-sm text-gray-700">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredIntakeLinks.length)} of {filteredIntakeLinks.length} results
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg ${
                        page === currentPage
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

      {/* Add/Edit Link Modal */}
      {(showAddLinkModal || editingLink) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Link className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      {editingLink ? 'Edit Intake Link' : 'Add New Intake Link'}
                    </h2>
                    <p className="text-blue-100 text-sm mt-1">
                      {editingLink ? 'Update the intake link configuration' : 'Configure a new Formstack intake link'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAddLinkModal(false)
                    setEditingLink(null)
                    setModalError(null)
                    resetFormData()
                  }}
                  className="text-white hover:text-blue-200 transition-colors duration-200 p-2 hover:bg-white/10 rounded-lg"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                {/* Left Column - Basic Info */}
                <div className="space-y-8">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Link className="w-4 h-4 text-blue-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                    </div>
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Link Alias (Public View) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.alias}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, alias: e.target.value }))
                            if (fieldErrors.alias) {
                              setFieldErrors(prev => ({ ...prev, alias: '' }))
                            }
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                            fieldErrors.alias ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          placeholder="Enter public alias for sales reps"
                        />
                        {fieldErrors.alias && (
                          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {fieldErrors.alias}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Law Firm Name (Internal) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.lawFirmName}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, lawFirmName: e.target.value }))
                            if (fieldErrors.lawFirmName) {
                              setFieldErrors(prev => ({ ...prev, lawFirmName: '' }))
                            }
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                            fieldErrors.lawFirmName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          placeholder="Enter law firm name"
                        />
                        {fieldErrors.lawFirmName && (
                          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {fieldErrors.lawFirmName}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Law Firm Link (Internal)
                        </label>
                        <input
                          type="url"
                          value={formData.lawFirmLink}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, lawFirmLink: e.target.value }))
                            if (fieldErrors.lawFirmLink) {
                              setFieldErrors(prev => ({ ...prev, lawFirmLink: '' }))
                            }
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                            fieldErrors.lawFirmLink ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          placeholder="https://lawfirm.com (optional)"
                        />
                        {fieldErrors.lawFirmLink && (
                          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {fieldErrors.lawFirmLink}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Intake Form URL <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="url"
                          required
                          value={formData.intakeFormUrl}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, intakeFormUrl: e.target.value }))
                            if (fieldErrors.intakeFormUrl) {
                              setFieldErrors(prev => ({ ...prev, intakeFormUrl: '' }))
                            }
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                            fieldErrors.intakeFormUrl ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          placeholder="https://formstack.com/intake-form"
                        />
                        {fieldErrors.intakeFormUrl && (
                          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {fieldErrors.intakeFormUrl}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Client Eligibility */}
                  <div className="bg-green-50 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <Users className="w-4 h-4 text-green-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Client Eligibility</h3>
                    </div>
                    <div className="space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Min Age <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.clientEligibility.age.min}
                            onChange={(e) => {
                              const inputValue = e.target.value
                              // Allow empty input or positive integers
                              const isValidNumber = /^\d*$/.test(inputValue)
                              
                              if (isValidNumber) {
                                setFormData(prev => ({ 
                                  ...prev, 
                                  clientEligibility: { 
                                    ...prev.clientEligibility, 
                                    age: { ...prev.clientEligibility.age, min: inputValue }
                                  }
                                }))
                                // Clear error when user starts typing
                                if (fieldErrors.minAge) {
                                  setFieldErrors(prev => ({ ...prev, minAge: '' }))
                                }
                              }
                            }}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                              fieldErrors.minAge ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            placeholder="18"
                          />
                          {fieldErrors.minAge && (
                            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {fieldErrors.minAge}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Max Age <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.clientEligibility.age.max}
                            onChange={(e) => {
                              const inputValue = e.target.value
                              // Allow empty input or positive integers
                              const isValidNumber = /^\d*$/.test(inputValue)
                              
                              if (isValidNumber) {
                                setFormData(prev => ({ 
                                  ...prev, 
                                  clientEligibility: { 
                                    ...prev.clientEligibility, 
                                    age: { ...prev.clientEligibility.age, max: inputValue }
                                  }
                                }))
                                // Clear error when user starts typing
                                if (fieldErrors.maxAge) {
                                  setFieldErrors(prev => ({ ...prev, maxAge: '' }))
                                }
                              }
                            }}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                              fieldErrors.maxAge ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            placeholder="65"
                          />
                          {fieldErrors.maxAge && (
                            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {fieldErrors.maxAge}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Gender <span className="text-red-500">*</span>
                        </label>
                        <select 
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                            fieldErrors.gender ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          onChange={(e) => {
                            setFormData(prev => ({ 
                              ...prev, 
                              clientEligibility: { 
                                ...prev.clientEligibility, 
                                gender: [e.target.value]
                              }
                            }))
                            if (fieldErrors.gender) {
                              setFieldErrors(prev => ({ ...prev, gender: '' }))
                            }
                          }}
                        >
                          <option value="">Select Gender</option>
                          <option value="all">All Genders</option>
                          <option value="male">Male Only</option>
                          <option value="female">Female Only</option>
                        </select>
                        {fieldErrors.gender && (
                          <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {fieldErrors.gender}
                          </p>
                        )}
                      </div>
                      <MultiSelectDropdown
                        label="Countries *"
                        options={COUNTRY_OPTIONS}
                        selectedValues={formData.clientEligibility.country}
                        onChange={(values) => {
                          setFormData(prev => ({ 
                          ...prev, 
                          clientEligibility: { 
                            ...prev.clientEligibility, 
                            country: values
                          }
                          }))
                          if (fieldErrors.countries) {
                            setFieldErrors(prev => ({ ...prev, countries: '' }))
                          }
                        }}
                        placeholder="Select countries..."
                      />
                      {fieldErrors.countries && (
                        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {fieldErrors.countries}
                        </p>
                      )}
                      <MultiSelectDropdown
                        label="States *"
                        options={STATE_OPTIONS}
                        selectedValues={formData.clientEligibility.states}
                        onChange={(values) => {
                          setFormData(prev => ({ 
                          ...prev, 
                          clientEligibility: { 
                            ...prev.clientEligibility, 
                            states: values
                          }
                          }))
                          if (fieldErrors.states) {
                            setFieldErrors(prev => ({ ...prev, states: '' }))
                          }
                        }}
                        placeholder="Select states..."
                      />
                      {fieldErrors.states && (
                        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {fieldErrors.states}
                        </p>
                      )}
                      <MultiSelectDropdown
                        label="Game Systems *"
                        options={GAME_SYSTEM_OPTIONS}
                        selectedValues={formData.clientEligibility.gameSystem}
                        onChange={(values) => {
                          setFormData(prev => ({ 
                          ...prev, 
                          clientEligibility: { 
                            ...prev.clientEligibility, 
                            gameSystem: values
                          }
                          }))
                          if (fieldErrors.gameSystems) {
                            setFieldErrors(prev => ({ ...prev, gameSystems: '' }))
                          }
                        }}
                        placeholder="Select game systems..."
                      />
                      {fieldErrors.gameSystems && (
                        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {fieldErrors.gameSystems}
                        </p>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Zipcodes <span className="text-red-500">*</span>
                        </label>
                        
                        {/* Allow All Zipcodes Checkbox */}
                        <div className="mb-4">
                          <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-green-300 transition-all duration-200">
                            <input
                              type="checkbox"
                              checked={formData.clientEligibility.allZipcodes}
                              onChange={(e) => {
                                setFormData(prev => ({
                                  ...prev,
                                  clientEligibility: {
                                    ...prev.clientEligibility,
                                    allZipcodes: e.target.checked,
                                    zipcodes: e.target.checked ? [] : prev.clientEligibility.zipcodes
                                  }
                                }))
                                if (e.target.checked) {
                                  setZipcodeInput('')
                                }
                                if (fieldErrors.zipcodes) {
                                  setFieldErrors(prev => ({ ...prev, zipcodes: '' }))
                                }
                              }}
                              className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            <div>
                              <div className="font-semibold text-gray-900">✅ Allow ALL Zipcodes</div>
                              <div className="text-sm text-gray-600">
                                No zipcode restrictions - accept any zipcode from any location
                              </div>
                            </div>
                          </label>
                        </div>

                        {/* Conditional Rendering: Only show manual input if allZipcodes is FALSE */}
                        {!formData.clientEligibility.allZipcodes && (
                          <div>
                            <p className="text-sm text-gray-600 mb-3">Enter zipcodes manually or auto-fill based on selected countries/states</p>
                            
                            {/* Manual Zipcode Input */}
                            <div className="space-y-3">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={zipcodeInput}
                              onChange={(e) => setZipcodeInput(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                  // Parse multiple zipcodes - support commas, spaces
                                  const zipcodes = zipcodeInput
                                    .split(/[,\s]+/)
                                    .map(z => z.trim())
                                    .filter(z => z && z.length > 0)
                                  
                                  if (zipcodes.length > 0) {
                                    // Merge with existing zipcodes (avoid duplicates)
                                    const allZipcodes = [...formData.clientEligibility.zipcodes, ...zipcodes]
                                    const uniqueZipcodes = Array.from(new Set(allZipcodes))
                                    
                                    setFormData(prev => ({ 
                                      ...prev, 
                                      clientEligibility: { 
                                        ...prev.clientEligibility, 
                                        zipcodes: uniqueZipcodes
                                      }
                                    }))
                                    setZipcodeInput('')
                                    if (fieldErrors.zipcodes) {
                                      setFieldErrors(prev => ({ ...prev, zipcodes: '' }))
                                    }
                                  }
                                }
                              }}
                              className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                                fieldErrors.zipcodes ? 'border-red-300 bg-red-50' : 'border-gray-300'
                              }`}
                              placeholder="Enter zipcode(s) separated by commas or spaces, then click Add"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                // Parse multiple zipcodes - support commas, spaces
                                const zipcodes = zipcodeInput
                                  .split(/[,\s]+/)
                                  .map(z => z.trim())
                                  .filter(z => z && z.length > 0)
                                
                                if (zipcodes.length > 0) {
                                  // Merge with existing zipcodes (avoid duplicates)
                                  const allZipcodes = [...formData.clientEligibility.zipcodes, ...zipcodes]
                                  const uniqueZipcodes = Array.from(new Set(allZipcodes))
                                  
                                  setFormData(prev => ({ 
                                    ...prev, 
                                    clientEligibility: { 
                                      ...prev.clientEligibility, 
                                      zipcodes: uniqueZipcodes
                                    }
                                  }))
                                  setZipcodeInput('')
                                  if (fieldErrors.zipcodes) {
                                    setFieldErrors(prev => ({ ...prev, zipcodes: '' }))
                                  }
                                }
                              }}
                              className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2 whitespace-nowrap"
                            >
                              <Plus className="w-4 h-4" />
                              Add
                            </button>
                          </div>
                          
                          {/* Auto-fill Button */}
                          <button
                            type="button"
                            onClick={() => {
                              const autoZipcodes = generateZipcodesForSelection()
                              if (autoZipcodes.length > 0) {
                                // Merge with existing zipcodes (avoid duplicates)
                                const allZipcodes = [...formData.clientEligibility.zipcodes, ...autoZipcodes]
                                const uniqueZipcodes = Array.from(new Set(allZipcodes))
                                
                                setFormData(prev => ({ 
                                  ...prev, 
                                  clientEligibility: { 
                                    ...prev.clientEligibility, 
                                    zipcodes: uniqueZipcodes
                                  }
                                }))
                                
                                if (fieldErrors.zipcodes) {
                                  setFieldErrors(prev => ({ ...prev, zipcodes: '' }))
                                }
                              }
                            }}
                            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Auto-fill Zipcodes for Selected Countries/States
                          </button>
                        </div>
                        
                        {/* Zipcode Tags Display */}
                        {formData.clientEligibility.zipcodes.length > 0 && (
                          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-green-900">
                                {formData.clientEligibility.zipcodes.length} Zipcode{formData.clientEligibility.zipcodes.length !== 1 ? 's' : ''} Added
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData(prev => ({ 
                                    ...prev, 
                                    clientEligibility: { 
                                      ...prev.clientEligibility, 
                                      zipcodes: []
                                    }
                                  }))
                                }}
                                className="text-xs text-red-600 hover:text-red-800 font-medium"
                              >
                                Clear All
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                              {formData.clientEligibility.zipcodes.map((zip, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-300"
                                >
                                  {zip}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData(prev => ({ 
                                        ...prev, 
                                        clientEligibility: { 
                                          ...prev.clientEligibility, 
                                          zipcodes: prev.clientEligibility.zipcodes.filter((_, i) => i !== index)
                                        }
                                      }))
                                    }}
                                    className="ml-2 text-green-600 hover:text-green-800"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                          </div>
                        )}
                        
                        {/* Show helpful message when All Zipcodes is checked */}
                        {formData.clientEligibility.allZipcodes && (
                          <div className="mt-4 p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Check className="w-5 h-5 text-green-600" />
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-green-800">All Zipcodes Allowed</h4>
                                <p className="text-sm text-green-700 mt-1">
                                  This intake link will accept clients from ANY zipcode without restrictions. 
                                  No manual zipcode validation will be performed.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {fieldErrors.zipcodes && (
                          <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {fieldErrors.zipcodes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Quota Metrics */}
                <div className="space-y-8">
                  <div className="bg-purple-50 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-purple-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Quota Metrics</h3>
                    </div>
                    <div className="space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Total Sales Allowed <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.quotaMetrics.totalSalesAllowed}
                            onChange={(e) => {
                              const inputValue = e.target.value
                              // Allow empty input or positive integers
                              const isValidNumber = /^\d*$/.test(inputValue)
                              
                              if (isValidNumber) {
                                setFormData(prev => ({ 
                                  ...prev, 
                                  quotaMetrics: { 
                                    ...prev.quotaMetrics, 
                                    totalSalesAllowed: inputValue
                                  }
                                }))
                                // Clear error when user starts typing
                                if (fieldErrors.totalSalesAllowed) {
                                  setFieldErrors(prev => ({ ...prev, totalSalesAllowed: '' }))
                                }
                              }
                            }}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                              fieldErrors.totalSalesAllowed ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            placeholder="100"
                          />
                          {fieldErrors.totalSalesAllowed && (
                            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {fieldErrors.totalSalesAllowed}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Remaining Sales Needed <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.quotaMetrics.remainingSalesNeeded}
                            onChange={(e) => {
                              const inputValue = e.target.value
                              // Allow empty input or positive integers
                              const isValidNumber = /^\d*$/.test(inputValue)
                              
                              if (isValidNumber) {
                                setFormData(prev => ({ 
                                  ...prev, 
                                  quotaMetrics: { 
                                    ...prev.quotaMetrics, 
                                    remainingSalesNeeded: inputValue
                                  }
                                }))
                                // Clear error when user starts typing
                                if (fieldErrors.remainingSalesNeeded) {
                                  setFieldErrors(prev => ({ ...prev, remainingSalesNeeded: '' }))
                                }
                              }
                            }}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                              fieldErrors.remainingSalesNeeded ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            placeholder="25"
                          />
                          {fieldErrors.remainingSalesNeeded && (
                            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {fieldErrors.remainingSalesNeeded}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                        <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors">
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Due Date for Quota <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={formData.quotaMetrics.dueDate}
                          onChange={(e) => {
                            setFormData(prev => ({ 
                            ...prev, 
                            quotaMetrics: { 
                              ...prev.quotaMetrics, 
                              dueDate: e.target.value
                            }
                            }))
                            // Clear error when user starts typing
                            if (fieldErrors.dueDate) {
                              setFieldErrors(prev => ({ ...prev, dueDate: '' }))
                            }
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                            fieldErrors.dueDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                        />
                        {fieldErrors.dueDate && (
                          <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {fieldErrors.dueDate}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Max Clients per Intake <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.quotaMetrics.maxClientsPerIntake}
                          onChange={(e) => {
                            const inputValue = e.target.value
                            // Allow empty input or positive integers
                            const isValidNumber = /^\d*$/.test(inputValue)
                            
                            if (isValidNumber) {
                              setFormData(prev => ({ 
                                ...prev, 
                                quotaMetrics: { 
                                  ...prev.quotaMetrics, 
                                  maxClientsPerIntake: inputValue
                                }
                              }))
                              // Clear error when user starts typing
                              if (fieldErrors.maxClientsPerIntake) {
                                setFieldErrors(prev => ({ ...prev, maxClientsPerIntake: '' }))
                              }
                            }
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                            fieldErrors.maxClientsPerIntake ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          placeholder="50"
                        />
                        {fieldErrors.maxClientsPerIntake && (
                          <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {fieldErrors.maxClientsPerIntake}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Priority of Fulfillment <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.quotaMetrics.priorityOfFulfillment}
                          onChange={(e) => {
                            const inputValue = e.target.value
                            // Allow empty input or positive integers
                            const isValidPriority = /^[1-9]\d*$/.test(inputValue) || inputValue === ''
                            
                            if (isValidPriority) {
                              setFormData(prev => ({ 
                                ...prev, 
                                quotaMetrics: { 
                                  ...prev.quotaMetrics, 
                                  priorityOfFulfillment: inputValue
                                }
                              }))
                              // Clear error when user starts typing
                              if (fieldErrors.priorityOfFulfillment) {
                                setFieldErrors(prev => ({ ...prev, priorityOfFulfillment: '' }))
                              }
                            }
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                            fieldErrors.priorityOfFulfillment ? 'border-red-300 bg-red-50' : 'border-gray-300 focus:border-purple-500'
                          }`}
                          placeholder="1"
                        />
                        <div className="mt-1">
                          <p className="text-xs text-gray-500">
                            <span className="font-medium">Note:</span> Priority 1 is the <span className="font-semibold text-purple-600">highest</span> priority (most urgent)
                          </p>
                          {fieldErrors.priorityOfFulfillment && (
                            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {fieldErrors.priorityOfFulfillment}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Assignment <span className="text-red-500">*</span></label>
                        {selectedCampaign ? (
                          <div className="flex items-center space-x-2 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg">
                            <span className="text-sm font-medium text-primary-900">{selectedCampaign.name}</span>
                          </div>
                        ) : (
                          <div className={`flex items-center space-x-2 px-4 py-3 border rounded-lg ${
                            fieldErrors.campaignAssignment ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                          }`}>
                            <AlertCircle className={`w-4 h-4 ${fieldErrors.campaignAssignment ? 'text-red-600' : 'text-amber-600'}`} />
                            <span className={`text-sm ${fieldErrors.campaignAssignment ? 'text-red-800' : 'text-amber-800'}`}>
                              {fieldErrors.campaignAssignment || 'No campaign selected'}
                            </span>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Bonus per Qualified Sale <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.quotaMetrics.bonusPerQualifiedSale}
                          onChange={(e) => {
                            const inputValue = e.target.value
                            // Allow empty input or positive integers
                            const isValidNumber = /^\d*$/.test(inputValue)
                            
                            if (isValidNumber) {
                              setFormData(prev => ({ 
                                ...prev, 
                                quotaMetrics: { 
                                  ...prev.quotaMetrics, 
                                  bonusPerQualifiedSale: inputValue
                                }
                              }))
                              // Clear error when user starts typing
                              if (fieldErrors.bonusPerQualifiedSale) {
                                setFieldErrors(prev => ({ ...prev, bonusPerQualifiedSale: '' }))
                              }
                            }
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                            fieldErrors.bonusPerQualifiedSale ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          placeholder="150"
                        />
                        {fieldErrors.bonusPerQualifiedSale && (
                          <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {fieldErrors.bonusPerQualifiedSale}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-8 py-6 border-t border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {editingLink ? 'Update the intake link configuration' : 'All fields are required to create a new intake link'}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowAddLinkModal(false)
                      setEditingLink(null)
                      setModalError(null)
                      resetFormData()
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-all duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // Clear previous errors
                      setFieldErrors({
                        alias: '',
                        lawFirmName: '',
                        lawFirmLink: '',
                        intakeFormUrl: '',
                        minAge: '',
                        maxAge: '',
                        gender: '',
                        countries: '',
                        states: '',
                        gameSystems: '',
                        zipcodes: '',
                        totalSalesAllowed: '',
                        remainingSalesNeeded: '',
                        maxClientsPerIntake: '',
                        priorityOfFulfillment: '',
                        bonusPerQualifiedSale: '',
                        dueDate: '',
                        campaignAssignment: ''
                      })
                      setModalError(null)
                      
                      let hasError = false
                      const errors = {
                        alias: '',
                        lawFirmName: '',
                        lawFirmLink: '',
                        intakeFormUrl: '',
                        minAge: '',
                        maxAge: '',
                        gender: '',
                        countries: '',
                        states: '',
                        gameSystems: '',
                        zipcodes: '',
                        totalSalesAllowed: '',
                        remainingSalesNeeded: '',
                        maxClientsPerIntake: '',
                        priorityOfFulfillment: '',
                        bonusPerQualifiedSale: '',
                        dueDate: '',
                        campaignAssignment: ''
                      }
                      
                      // Validate required fields for both Add and Update
                        if (!formData.alias.trim()) {
                        errors.alias = 'Link Alias (Public View) is required'
                        hasError = true
                        }
                        
                        if (!formData.lawFirmName.trim()) {
                        errors.lawFirmName = 'Law Firm Name (Internal) is required'
                        hasError = true
                      }
                      
                      // Law Firm Link is optional, but validate URL format if provided
                      if (formData.lawFirmLink.trim()) {
                        try {
                          new URL(formData.lawFirmLink)
                        } catch {
                          errors.lawFirmLink = 'Must be a valid URL (e.g., https://lawfirm.com)'
                          hasError = true
                        }
                      }
                      
                      if (!formData.intakeFormUrl.trim()) {
                        errors.intakeFormUrl = 'Intake Form URL is required'
                        hasError = true
                      } else {
                        // Validate URL format for Intake Form URL
                        try {
                          new URL(formData.intakeFormUrl)
                        } catch {
                          errors.intakeFormUrl = 'Must be a valid URL (e.g., https://formstack.com/intake-form)'
                          hasError = true
                        }
                      }
                      
                      // Validate Client Eligibility fields
                      if (!formData.clientEligibility.age.min.trim()) {
                        errors.minAge = 'Min Age is required'
                        hasError = true
                      } else if (Number(formData.clientEligibility.age.min) < 0) {
                        errors.minAge = 'Min Age must be a positive number'
                        hasError = true
                      }
                      
                      if (!formData.clientEligibility.age.max.trim()) {
                        errors.maxAge = 'Max Age is required'
                        hasError = true
                      } else if (Number(formData.clientEligibility.age.max) < 0) {
                        errors.maxAge = 'Max Age must be a positive number'
                        hasError = true
                      } else if (formData.clientEligibility.age.min.trim() && Number(formData.clientEligibility.age.max) < Number(formData.clientEligibility.age.min)) {
                        errors.maxAge = 'Max Age must be greater than Min Age'
                        hasError = true
                      }
                      
                      // Validate dropdown fields
                      if (!formData.clientEligibility.gender || formData.clientEligibility.gender.length === 0) {
                        errors.gender = 'Gender selection is required'
                        hasError = true
                      }
                      
                      if (!formData.clientEligibility.country || formData.clientEligibility.country.length === 0) {
                        errors.countries = 'Country selection is required'
                        hasError = true
                      }
                      
                      if (!formData.clientEligibility.states || formData.clientEligibility.states.length === 0) {
                        errors.states = 'State selection is required'
                        hasError = true
                      }
                      
                      if (!formData.clientEligibility.gameSystem || formData.clientEligibility.gameSystem.length === 0) {
                        errors.gameSystems = 'Game System selection is required'
                        hasError = true
                      }
                      
                      if (!formData.clientEligibility.allZipcodes) {
                        // Only validate zipcodes if "Allow ALL Zipcodes" is NOT checked
                        if (!formData.clientEligibility.zipcodes || formData.clientEligibility.zipcodes.length === 0) {
                          errors.zipcodes = 'Zipcode selection is required (or enable "Allow ALL Zipcodes")'
                          hasError = true
                        }
                      }
                      
                      // Validate Quota Metrics fields
                      if (!formData.quotaMetrics.totalSalesAllowed.trim()) {
                        errors.totalSalesAllowed = 'Total Sales Allowed is required'
                        hasError = true
                      } else if (Number(formData.quotaMetrics.totalSalesAllowed) <= 0) {
                        errors.totalSalesAllowed = 'Must be a positive number'
                        hasError = true
                      }
                      
                      if (!formData.quotaMetrics.remainingSalesNeeded.trim()) {
                        errors.remainingSalesNeeded = 'Remaining Sales Needed is required'
                        hasError = true
                      } else if (Number(formData.quotaMetrics.remainingSalesNeeded) < 0) {
                        errors.remainingSalesNeeded = 'Must be a positive number'
                        hasError = true
                      }
                      
                      if (!formData.quotaMetrics.maxClientsPerIntake.trim()) {
                        errors.maxClientsPerIntake = 'Max Clients per Intake is required'
                        hasError = true
                      } else if (Number(formData.quotaMetrics.maxClientsPerIntake) <= 0) {
                        errors.maxClientsPerIntake = 'Must be a positive number'
                        hasError = true
                      }
                      
                      if (!formData.quotaMetrics.priorityOfFulfillment || formData.quotaMetrics.priorityOfFulfillment.toString().trim() === '') {
                        errors.priorityOfFulfillment = 'Priority of Fulfillment is required'
                        hasError = true
                      } else if (!/^[1-9]\d*$/.test(formData.quotaMetrics.priorityOfFulfillment.toString())) {
                        errors.priorityOfFulfillment = 'Must be a positive integer (1, 2, 3, etc.)'
                        hasError = true
                      }
                      
                      if (!formData.quotaMetrics.bonusPerQualifiedSale.trim()) {
                        errors.bonusPerQualifiedSale = 'Bonus per Qualified Sale is required'
                        hasError = true
                      } else if (Number(formData.quotaMetrics.bonusPerQualifiedSale) < 0) {
                        errors.bonusPerQualifiedSale = 'Must be a positive number'
                        hasError = true
                      }
                      
                      // Validate Due Date
                      if (!formData.quotaMetrics.dueDate.trim()) {
                        errors.dueDate = 'Due Date for Quota is required'
                        hasError = true
                      }
                      
                      // Validate Campaign Assignment
                      if (!selectedCampaign) {
                        errors.campaignAssignment = 'Campaign assignment is required'
                        hasError = true
                      }
                      
                      if (hasError) {
                        setFieldErrors(errors)
                          return
                        }
                        
                      if (editingLink) {
                        updateIntakeLink()
                      } else {
                        addIntakeLink(formData)
                      }
                    }}
                    disabled={actionLoading === 'create' || actionLoading === editingLink?.id}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {actionLoading === 'create' || actionLoading === editingLink?.id ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="animate-pulse">
                          {editingLink ? 'Updating...' : 'Creating...'}
                        </span>
                      </div>
                    ) : (
                      editingLink ? 'Update Link' : 'Add Link'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}
